import React, { useEffect, useMemo, useState } from "react";
import { PurchaseOrderDetail, ReceiveRequest } from "../types";
import { purchasingService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { todayLocal } from "../utils/dates";
import { poErr, poFmtQty, poInput, poNum, poParse } from "../utils/purchasing";
import "../pages/PurchaseOrders.css";

interface Props {
  order: PurchaseOrderDetail;
  onClose: () => void;
  onSaved: () => void;
}

const today = () => todayLocal();
/** Sunucu 3 basamağa yuvarlar; kontrol de yuvarlanmış değerle yapılır. */
const parse = (s: string) => Math.round(poNum(s) * 1000) / 1000;
const bad = (s: string | undefined) => !!s && s.trim() !== "" && isNaN(poParse(s));
const closeIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>;

/** Mal kabul: satır bazında teslim alınan miktar; kalanı aşamaz. */
const ReceiveModal: React.FC<Props> = ({ order, onClose, onSaved }) => {
  const { hasModule } = usePermissions();
  const open = useMemo(() => order.lines.filter((l) => l.remaining > 0.0005), [order.lines]);
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, poInput(l.remaining)])));
  const [receiptDate, setReceiptDate] = useState(today());
  const [waybillNo, setWaybillNo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totalLines = open.filter((l) => parse(qty[l.purchaseOrderLineId]) > 0).length;
  const fillAll = () => setQty(Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, poInput(l.remaining)])));
  const clearAll = () => setQty(Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, ""])));

  const save = async () => {
    if (saving) return;
    if (!receiptDate) return setError("Kabul tarihi girin.");
    if (receiptDate > today()) return setError("Kabul tarihi gelecekte olamaz.");
    const badLine = open.find((l) => bad(qty[l.purchaseOrderLineId]));
    if (badLine) return setError(`${badLine.stockName}: miktar geçerli bir sayı değil (örn. 1250 ya da 1.250,5).`);
    const lines = open
      .map((l) => ({ purchaseOrderLineId: l.purchaseOrderLineId, quantity: parse(qty[l.purchaseOrderLineId]) }))
      .filter((l) => l.quantity > 0);
    if (lines.length === 0) return setError("En az bir satırda miktar girin.");
    for (const l of lines) {
      const src = open.find((o) => o.purchaseOrderLineId === l.purchaseOrderLineId)!;
      if (l.quantity > src.remaining + 0.0005) return setError(`${src.stockName}: kalan ${poFmtQty(src.remaining)} ${src.unit} aşılamaz.`);
    }
    const dto: ReceiveRequest = { receiptDate, waybillNo: waybillNo.trim() || null, note: note.trim() || null, lines };
    setSaving(true); setError("");
    try { await purchasingService.receive(order.purchaseOrderId, dto); onSaved(); onClose(); }
    catch (err: any) { setError(poErr(err, "Mal kabul kaydedilemedi.")); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) { e.stopPropagation(); onClose(); }
      else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, saving, qty, receiptDate, waybillNo, note]);

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="po-recv-title">
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">{order.number} · {order.supplierName}</span>
            <h3 id="po-recv-title">Mal kabul</h3>
            {!hasModule("stock") && <div className="po-badges"><span className="po-badge muted" title="Stok modülü lisansta yok">stok hareketi üretilmez</span></div>}
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{closeIcon}</button>
        </div>

        <div className="po-modal-body">
          {error && <div className="po-note err" role="alert">{error}</div>}
          <div className="po-grid3">
            <label className="po-field"><span>Kabul tarihi</span><input type="date" value={receiptDate} max={today()} onChange={(e) => setReceiptDate(e.target.value)} /></label>
            <label className="po-field"><span>İrsaliye no<em>isteğe bağlı</em></span><input value={waybillNo} maxLength={50} autoFocus onChange={(e) => setWaybillNo(e.target.value)} /></label>
            <label className="po-field"><span>Not<em>isteğe bağlı</em></span><input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} /></label>
          </div>

          <div className="po-actrow">
            <button type="button" className="po-btn small" onClick={fillAll}>Kalanın tamamı</button>
            <button type="button" className="po-btn small" onClick={clearAll}>Temizle</button>
          </div>

          <div className="po-table-wrap tight">
            <table className="po-table small">
              <thead><tr><th>#</th><th>Stok</th><th className="num">Sipariş</th><th className="num">Alınan</th><th className="num">Kalan</th><th className="num">Teslim alınan</th></tr></thead>
              <tbody>
                {open.map((l, i) => {
                  const over = parse(qty[l.purchaseOrderLineId]) > l.remaining + 0.0005;
                  const invalid = over || bad(qty[l.purchaseOrderLineId]);
                  return (
                    <tr key={l.purchaseOrderLineId} className="static">
                      <td>{i + 1}</td>
                      <td>
                        <span className="po-name">{l.stockName}</span>{l.stockCode && <span className="po-codepill">{l.stockCode}</span>}
                        {l.description && <div className="po-sub">{l.description}</div>}
                      </td>
                      <td className="num">{poFmtQty(l.quantity)} {l.unit}</td>
                      <td className="num">{poFmtQty(l.receivedQuantity)}</td>
                      <td className="num"><b>{poFmtQty(l.remaining)}</b></td>
                      <td className="num">
                        <div className="po-money cell">
                          <input inputMode="decimal" aria-label={`${l.stockName} teslim alınan miktar`} className={invalid ? "is-invalid" : ""} value={qty[l.purchaseOrderLineId] ?? ""} onFocus={(e) => e.currentTarget.select()}
                            onChange={(e) => { setQty({ ...qty, [l.purchaseOrderLineId]: e.target.value }); setError(""); }} />
                          <i>{l.unit}</i>
                        </div>
                        {over && <div className="po-fielderr">kalan {poFmtQty(l.remaining)} aşılamaz</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="po-note info">
            {hasModule("stock")
              ? "Teslim alınan miktarlar stoğa giriş olarak işlenir; stoğun son alış fiyatı ve tedarikçisi güncellenir."
              : "Stok modülü kapalı; yalnız teslim miktarı izlenir, stok bakiyesi değişmez."}
          </div>
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint"><b>{totalLines}</b> satır teslim alınacak</span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="button" className="po-btn primary" onClick={save} disabled={saving || totalLines === 0}>{saving ? "Kaydediliyor" : "Mal kabulü kaydet"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiveModal;
